import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WizardStepper from './WizardStepper';
import WizardErrorBoundary from './WizardErrorBoundary';
import StorageProviderStep, { type StorageProviderData } from './steps/StorageProviderStep';
import GitHubInfraStep, { type GitHubInfraData } from './steps/GitHubInfraStep';
import Step2InfrastructureMode, { type InfrastructureModeData } from './steps/Step2InfrastructureMode';
import BYOCCredentialsStep, { type BYOCCredentialsData } from './steps/BYOCCredentialsStep';
import Step3DeploymentPolicy, { type Step2Data as Step3Data } from './steps/Step2DeploymentPolicy';
import Step4QAPolicy, { type QAPolicyData } from './steps/Step3QAPolicy';
import Step5AgentConfig, { type AgentConfigData } from './steps/Step4AgentConfig';
import Step6ReviewConfirm, { type Step5Data as Step6Data } from './steps/Step5ReviewConfirm';
import ManagedSetupStep, { type ManagedSetupData } from './steps/ManagedSetupStep';
import EstimationPreferenceStep, { type EstimationPreferenceData } from './steps/EstimationPreferenceStep';

const WIZARD_STEPS_BYOC = [
  { id: 'storage',             label: 'Storage' },
  { id: 'github',              label: 'GitHub' },
  { id: 'infrastructure_mode', label: 'Deployment Mode' },
  { id: 'byoc_credentials',    label: 'Cloud Credentials' },
  { id: 'deployment',          label: 'Deployment' },
  { id: 'qa_policy',           label: 'QA Policy' },
  { id: 'estimation',          label: 'Estimation' },
  { id: 'agent_config',        label: 'Agent Config' },
  { id: 'review',              label: 'Review & Confirm' },
];

const WIZARD_STEPS_MANAGED = [
  { id: 'storage',             label: 'Storage' },
  { id: 'github',              label: 'GitHub' },
  { id: 'infrastructure_mode', label: 'Deployment Mode' },
  { id: 'managed_setup',       label: 'Managed Setup' },
  { id: 'qa_policy',           label: 'QA Policy' },
  { id: 'estimation',          label: 'Estimation' },
  { id: 'agent_config',        label: 'Agent Config' },
  { id: 'review',              label: 'Review & Confirm' },
];

interface WizardProgress {
  currentStep:    number;
  completedSteps: number[];
  stepData:       Record<string, unknown>;
  completed:      boolean;
}

interface WizardShellProps {
  tenantId:     string;
  initialStep?: number;
}

function storageKey(tenantId: string) {
  return `onboarding_progress_${tenantId}`;
}

function loadProgress(tenantId: string): WizardProgress | null {
  try {
    const raw = localStorage.getItem(storageKey(tenantId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WizardProgress;
    // Pre-#1582 sessions keyed stepData by positional number ("0","1","2").
    // Resume with those keys would map to wrong steps — discard them.
    if (parsed.stepData && Object.keys(parsed.stepData).some(k => /^\d+$/.test(k))) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveProgress(tenantId: string, progress: WizardProgress) {
  try {
    localStorage.setItem(storageKey(tenantId), JSON.stringify(progress));
  } catch {
    // ignore quota errors
  }
}

function clearProgress(tenantId: string) {
  localStorage.removeItem(storageKey(tenantId));
}

// ── Completion screen ─────────────────────────────────────────────────────────

function CompletionScreen({ onGoToDashboard }: { onGoToDashboard: () => void }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '48px 24px', textAlign: 'center',
    }}>
      <div style={{
        width: 72, height: 72, borderRadius: '50%',
        background: 'rgba(124,58,237,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24,
        animation: 'checkmark-pop 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
      }}>
        <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
          <circle cx="18" cy="18" r="17" stroke="var(--primary)" strokeWidth="2" />
          <path
            d="M10 18l6 6 10-12"
            stroke="var(--primary)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ animation: 'check-draw 0.4s 0.2s ease-out forwards', strokeDasharray: 25, strokeDashoffset: 25 }}
          />
        </svg>
      </div>

      <h2 style={{
        fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800,
        color: 'var(--text)', marginBottom: 8,
      }}>
        Your organisation is ready
      </h2>
      <p style={{
        fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--text-secondary)',
        marginBottom: 32, maxWidth: 360,
      }}>
        BluePrint is configured for your team.
      </p>

      <button
        onClick={onGoToDashboard}
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 15, fontWeight: 700,
          padding: '12px 32px',
          background: 'var(--primary)', color: '#fff',
          border: 'none', borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
        }}
      >
        Go to Dashboard
      </button>

      <style>{`
        @keyframes checkmark-pop {
          from { transform: scale(0.5); opacity: 0; }
          to   { transform: scale(1);   opacity: 1; }
        }
        @keyframes check-draw {
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}

// ── Main shell ────────────────────────────────────────────────────────────────

export default function WizardShell({ tenantId, initialStep }: WizardShellProps) {
  const navigate = useNavigate();

  const [state, setState] = useState<WizardProgress>(() => {
    const saved = loadProgress(tenantId);
    return saved ?? {
      currentStep:    initialStep ?? 0,
      completedSteps: [],
      stepData:       {},
      completed:      false,
    };
  });

  // Persist on every state change
  useEffect(() => {
    saveProgress(tenantId, state);
  }, [tenantId, state]);

  const [stepError, setStepError] = useState('');

  const isManaged = (state.stepData['infrastructure_mode'] as InfrastructureModeData | undefined)?.infrastructureMode === 'MANAGED';
  const WIZARD_STEPS = isManaged ? WIZARD_STEPS_MANAGED : WIZARD_STEPS_BYOC;

  const handleStepComplete = (data: unknown) => {
    setStepError('');
    setState(prev => {
      const prevManaged = (prev.stepData['infrastructure_mode'] as InfrastructureModeData | undefined)?.infrastructureMode === 'MANAGED';
      const prevSteps   = prevManaged ? WIZARD_STEPS_MANAGED : WIZARD_STEPS_BYOC;
      const stepId      = prevSteps[prev.currentStep]?.id ?? String(prev.currentStep);
      const managed     = prevManaged
        || (stepId === 'infrastructure_mode' && (data as InfrastructureModeData)?.infrastructureMode === 'MANAGED');
      const steps     = managed ? WIZARD_STEPS_MANAGED : WIZARD_STEPS_BYOC;
      const isLast    = prev.currentStep === steps.length - 1;
      const nextStep  = isLast ? prev.currentStep : prev.currentStep + 1;
      const completed = prev.completedSteps.includes(prev.currentStep)
        ? prev.completedSteps
        : [...prev.completedSteps, prev.currentStep];

      const next: WizardProgress = {
        currentStep:    nextStep,
        completedSteps: completed,
        stepData:       { ...prev.stepData, [stepId]: data },
        completed:      isLast,
      };

      if (isLast) {
        clearProgress(tenantId);
      }

      return next;
    });
  };

  const handleBack = () => {
    setState(prev => ({
      ...prev,
      currentStep: Math.max(0, prev.currentStep - 1),
    }));
  };

  const handleSaveLater = () => {
    saveProgress(tenantId, state);
    navigate('/projects');
  };

  const handleEditStep = (index: number) => {
    setState(prev => ({ ...prev, currentStep: index }));
  };

  // Show completion screen after last step finishes
  if (state.completed) {
    return <CompletionScreen onGoToDashboard={() => navigate('/projects')} />;
  }

  return (
    <WizardErrorBoundary>
    <div>
      <WizardStepper
        steps={WIZARD_STEPS}
        currentStep={state.currentStep}
        completedSteps={state.completedSteps}
        onStepClick={handleEditStep}
      />

      {/* Step error banner */}
      {stepError && (
        <div style={{
          margin: '12px 0', padding: '10px 14px',
          background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)', fontSize: 13,
          color: 'var(--error)',
        }}>
          {stepError}
        </div>
      )}

      {/* Step content — BYOC 9 steps / Managed 8 steps (B1 splits infra into
          storage(0)+github(1); mode is now step 2, all subsequent shift +1). */}
      {state.currentStep === 0 ? (
        <StorageProviderStep
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['storage'] as StorageProviderData | undefined}
        />
      ) : state.currentStep === 1 ? (
        <GitHubInfraStep
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['github'] as GitHubInfraData | undefined}
        />
      ) : state.currentStep === 2 ? (
        <Step2InfrastructureMode
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['infrastructure_mode'] as InfrastructureModeData | undefined}
        />
      ) : state.currentStep === 3 && isManaged ? (
        <ManagedSetupStep
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['managed_setup'] as ManagedSetupData | undefined}
        />
      ) : state.currentStep === 3 && !isManaged ? (
        <BYOCCredentialsStep
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['byoc_credentials'] as BYOCCredentialsData | undefined}
        />
      ) : state.currentStep === 4 && !isManaged ? (
        <Step3DeploymentPolicy
          tenantId={tenantId}
          onSaved={handleStepComplete}
          savedData={state.stepData['deployment'] as Step3Data | undefined}
        />
      ) : state.currentStep === 4 && isManaged ? (
        <Step4QAPolicy
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['qa_policy'] as QAPolicyData | undefined}
        />
      ) : state.currentStep === 5 && !isManaged ? (
        <Step4QAPolicy
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['qa_policy'] as QAPolicyData | undefined}
        />
      ) : state.currentStep === 5 && isManaged ? (
        <EstimationPreferenceStep
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['estimation'] as EstimationPreferenceData | undefined}
        />
      ) : state.currentStep === 6 && !isManaged ? (
        <EstimationPreferenceStep
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['estimation'] as EstimationPreferenceData | undefined}
        />
      ) : state.currentStep === 6 && isManaged ? (
        <Step5AgentConfig
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['agent_config'] as AgentConfigData | undefined}
          showModelPointer
        />
      ) : state.currentStep === 7 && !isManaged ? (
        <Step5AgentConfig
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['agent_config'] as AgentConfigData | undefined}
          showModelPointer
        />
      ) : state.currentStep === 7 && isManaged ? (
        <Step6ReviewConfirm
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['review'] as Step6Data | undefined}
          allStepData={state.stepData}
          allSteps={WIZARD_STEPS}
          onEditStep={handleEditStep}
        />
      ) : state.currentStep === 8 ? (
        <Step6ReviewConfirm
          tenantId={tenantId}
          onStepComplete={handleStepComplete}
          savedData={state.stepData['review'] as Step6Data | undefined}
          allStepData={state.stepData}
          allSteps={WIZARD_STEPS}
          onEditStep={handleEditStep}
        />
      ) : null}

      {/* Footer controls */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 24,
          marginTop: 24,
          borderTop: '1px solid var(--border)',
        }}
      >
        <button
          onClick={handleBack}
          disabled={state.currentStep === 0}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            fontWeight: 500,
            padding: '9px 20px',
            background: 'transparent',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            cursor: state.currentStep === 0 ? 'not-allowed' : 'pointer',
            opacity: state.currentStep === 0 ? 0.4 : 1,
          }}
        >
          ← Back
        </button>

        <button
          onClick={handleSaveLater}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 13,
            fontWeight: 500,
            padding: '9px 16px',
            background: 'transparent',
            color: 'var(--text-secondary)',
            border: 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--text)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
        >
          Save and continue later
        </button>
      </div>
    </div>
    </WizardErrorBoundary>
  );
}
